/**
 * ResizablePanel Component
 *
 * A reusable panel component that can be resized by dragging its edge.
 * Supports both left and right positions, with configurable min/max widths.
 * Persists user-chosen width in localStorage.
 */

import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ResizablePanelProps {
  /** Panel content */
  children: ReactNode;
  /** Position of the panel (affects which edge has the drag handle) */
  side: 'left' | 'right';
  /** Default width in pixels */
  defaultWidth: number;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Storage key for persisting width (optional, enables persistence) */
  storageKey?: string;
  /** Additional class names for the panel container */
  className?: string;
  /** Whether to hide on mobile (defaults to true) */
  hideOnMobile?: boolean;
  /** Custom mobile breakpoint class (defaults to 'md') */
  mobileBreakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  /** Callback when width changes */
  onWidthChange?: (width: number) => void;
  /** Whether the panel is collapsed */
  isCollapsed?: boolean;
  /** Callback when collapse state changes */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Show collapse button */
  showCollapseButton?: boolean;
  /** Width when collapsed (defaults to 0) */
  collapsedWidth?: number;
  /** ARIA label for the resize handle */
  resizeAriaLabel?: string;
}

/**
 * ResizablePanel - A panel that can be resized by dragging its edge
 */
export function ResizablePanel({
  children,
  side,
  defaultWidth,
  minWidth = 200,
  maxWidth = 600,
  storageKey,
  className,
  hideOnMobile = true,
  mobileBreakpoint = 'md',
  onWidthChange,
  isCollapsed = false,
  onCollapseChange,
  showCollapseButton = false,
  collapsedWidth = 0,
  resizeAriaLabel,
}: ResizablePanelProps) {
  // Initialize width from localStorage or default
  const [width, setWidth] = useState<number>(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`resizable-panel-${storageKey}`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
          return parsed;
        }
      }
    }
    return defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const lastTapTimeRef = useRef<number>(0);

  // Persist width to localStorage
  useEffect(() => {
    if (storageKey && !isCollapsed) {
      localStorage.setItem(`resizable-panel-${storageKey}`, width.toString());
    }
  }, [width, storageKey, isCollapsed]);

  // Notify parent of width changes
  useEffect(() => {
    onWidthChange?.(isCollapsed ? collapsedWidth : width);
  }, [width, isCollapsed, collapsedWidth, onWidthChange]);

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      // Add cursor style to body during resize
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width]
  );

  // Handle touch start on resize handle (with double-tap detection)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const now = Date.now();
      const DOUBLE_TAP_DELAY = 300; // ms

      // Check for double-tap to reset width
      if (now - lastTapTimeRef.current < DOUBLE_TAP_DELAY) {
        // Double-tap detected - reset to default width
        setWidth(defaultWidth);
        lastTapTimeRef.current = 0;
        return;
      }

      lastTapTimeRef.current = now;
      setIsResizing(true);
      startXRef.current = touch.clientX;
      startWidthRef.current = width;
    },
    [width, defaultWidth]
  );

  // Handle mouse/touch move during resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = side === 'left'
        ? e.clientX - startXRef.current
        : startXRef.current - e.clientX;

      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta));
      setWidth(newWidth);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const delta = side === 'left'
        ? touch.clientX - startXRef.current
        : startXRef.current - touch.clientX;

      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta));
      setWidth(newWidth);
    };

    const handleEnd = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isResizing, side, minWidth, maxWidth]);

  // Handle keyboard resize
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const step = e.shiftKey ? 50 : 10;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const delta = side === 'left' ? -step : step;
        setWidth((w) => Math.min(maxWidth, Math.max(minWidth, w + delta)));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const delta = side === 'left' ? step : -step;
        setWidth((w) => Math.min(maxWidth, Math.max(minWidth, w + delta)));
      }
    },
    [side, minWidth, maxWidth]
  );

  // Handle double-click to reset to default
  const handleDoubleClick = useCallback(() => {
    setWidth(defaultWidth);
  }, [defaultWidth]);

  // Determine the mobile visibility class
  const mobileVisibilityClass = hideOnMobile
    ? {
        sm: 'hidden sm:flex',
        md: 'hidden md:flex',
        lg: 'hidden lg:flex',
        xl: 'hidden xl:flex',
      }[mobileBreakpoint]
    : 'flex';

  // If collapsed, show minimal collapsed state
  if (isCollapsed) {
    return (
      <div
        ref={panelRef}
        className={cn(
          'flex-shrink-0 bg-card',
          side === 'left' ? 'border-r' : 'border-l',
          'border-border',
          mobileVisibilityClass,
          className
        )}
        style={{ width: collapsedWidth }}
      >
        {showCollapseButton && onCollapseChange && collapsedWidth > 0 && (
          <button
            onClick={() => { onCollapseChange(false); }}
            className="flex h-full w-full items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Expand panel"
            title="Expand"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={side === 'left' ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'}
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={cn(
        'relative flex-shrink-0 bg-card',
        side === 'left' ? 'border-r' : 'border-l',
        'border-border',
        mobileVisibilityClass,
        'flex-col',
        className
      )}
      style={{ width }}
    >
      {/* Content */}
      <div className="flex h-full w-full min-w-0 flex-col overflow-hidden">
        {children}
      </div>

      {/* Resize handle */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={resizeAriaLabel || `Resize ${side} panel`}
        aria-valuenow={width}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabIndex={0}
        className={cn(
          'absolute top-0 bottom-0 z-10 w-1 cursor-col-resize',
          'hover:bg-primary/50 active:bg-primary',
          'transition-colors duration-150',
          'touch-none',
          side === 'left' ? '-right-0.5' : '-left-0.5',
          isResizing && 'bg-primary'
        )}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onKeyDown={handleKeyDown}
        onDoubleClick={handleDoubleClick}
      >
        {/* Visual indicator on hover */}
        <div
          className={cn(
            'absolute top-1/2 -translate-y-1/2 flex flex-col gap-0.5',
            'opacity-0 hover:opacity-100 transition-opacity',
            side === 'left' ? '-right-1' : '-left-1'
          )}
        >
          <div className="h-1 w-1 rounded-full bg-muted-foreground" />
          <div className="h-1 w-1 rounded-full bg-muted-foreground" />
          <div className="h-1 w-1 rounded-full bg-muted-foreground" />
        </div>
      </div>

      {/* Resize overlay to capture mouse events during resize */}
      {isResizing && (
        <div className="fixed inset-0 z-50 cursor-col-resize" aria-hidden="true" />
      )}
    </div>
  );
}

export default ResizablePanel;
